import {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from "react";

import { useSearchParameters } from "@/components/signup/hooks";

import { TemplateOptionMetadata } from "../components/template-choice/template-definitions";

import { useConfirmationPage } from "./use-confirmation-page";

import { useFetchUserSegmentData } from "./";

export type JiraTemplateChoice =
    | "kanban"
    | "scrum"
    | "business"
    | "campaign management"
    | "project management"
    | "content management"
    | "process control"
    | "procurement"
    | "service management"
    | "product discovery"
    | undefined;

export const jiraTemplateChoiceTypeCheck = (
    s: string | undefined
): JiraTemplateChoice => {
    switch (s) {
        case "kanban":
        case "scrum":
        case "business":
        case "campaign management":
        case "project management":
        case "content management":
        case "process control":
        case "procurement":
        case "service management":
        case "product discovery":
            return s;
        default:
            return undefined;
    }
};

export type JiraTemplateChoiceProps = {
    shouldShowJiraTemplateChoiceScreen: boolean;
    loadingUserSegmentData: boolean;
    templateChoice?: JiraTemplateChoice;
    setTemplateChoice: (templateChoice?: JiraTemplateChoice) => void;
    templateChoiceId?: string;
    setTemplateChoiceId: (templateChoiceId?: string) => void;
    templateChoiceName?: string;
    setTemplateChoiceName: (templateChoiceName?: string) => void;
    templateChoiceError: Error | undefined;
    setTemplateChoiceError: (e: Error) => void;
    goBackToUserSegmentation: () => void;
    templateMetadata?: TemplateOptionMetadata;
    setTemplateMetadata: React.Dispatch<
        React.SetStateAction<TemplateOptionMetadata | undefined>
    >;
};

/**
 * TODO: refector this provider to only store template choice information and loadingUserSegmentData.
 * The viewing of this screen will be controlled by the jira screen config and usage of the useScreenManager hook
 * to navigate to the previous of next screen.
 * */
export const JiraTemplateChoiceContext = createContext<JiraTemplateChoiceProps>(
    {
        shouldShowJiraTemplateChoiceScreen: false,
        loadingUserSegmentData: false,
        templateChoice: undefined,
        setTemplateChoice: () => undefined,
        setTemplateChoiceId: () => undefined,
        setTemplateChoiceName: () => undefined,
        templateChoiceId: undefined,
        templateChoiceName: undefined,
        templateChoiceError: undefined,
        setTemplateChoiceError: () => undefined,
        goBackToUserSegmentation: () => undefined,
        templateMetadata: undefined,
        setTemplateMetadata: () => undefined
    }
);

export const JiraTemplateChoiceProvider = ({
    children
}: PropsWithChildren<unknown>) => {
    /**
     * TODO: Once the confirmation rearchitecture is complete, the hook useCanShowJiraTemplateChoiceScreen can be removed.
     *  This is because the jira screen config will be responsible for determining if the template choice screen should show.
     **/
    const {
        canShowJiraTemplateChoiceScreen,
        loadingUserSegmentData,
        setUserSegmentationPageIsFinished
    } = useCanShowJiraTemplateChoiceScreen();

    /**
     * TODO: Once the confirmation rearchitecture is complete, this function can be removed.
     * This is because the hook useScreenManager will provide the function to navigate back to the user segmentation view.
     **/
    const goBackToUserSegmentation = useCallback(
        () => setUserSegmentationPageIsFinished(false),
        []
    );

    const { templateChoice: templateChoiceQueryParam } = useSearchParameters();

    const [templateChoice, setTemplateChoice] = useState<
        JiraTemplateChoice | undefined
    >(jiraTemplateChoiceTypeCheck(templateChoiceQueryParam));

    const [templateChoiceId, setTemplateChoiceId] = useState<
        string | undefined
    >();

    const [templateChoiceName, setTemplateChoiceName] = useState<
        string | undefined
    >();

    const [templateMetadata, setTemplateMetadata] =
        useState<TemplateOptionMetadata>();

    const [templateChoiceError, setTemplateChoiceError] = useState<
        Error | undefined
    >();

    const shouldShowJiraTemplateChoiceScreen =
        templateChoiceError === undefined && canShowJiraTemplateChoiceScreen;

    return (
        <JiraTemplateChoiceContext.Provider
            value={{
                templateChoice,
                loadingUserSegmentData,
                setTemplateChoice,
                setTemplateChoiceId,
                setTemplateChoiceName,
                templateChoiceId,
                templateChoiceName,
                shouldShowJiraTemplateChoiceScreen,
                templateChoiceError,
                setTemplateChoiceError,
                goBackToUserSegmentation,
                templateMetadata,
                setTemplateMetadata
            }}
        >
            {children}
        </JiraTemplateChoiceContext.Provider>
    );
};

/*
 * The user must pass the following conditions to see the template choice screen
 */
export const useCanShowJiraTemplateChoiceScreen = () => {
    const {
        userSegmentationContext: {
            userSegmentationPageIsFinished,
            setUserSegmentationPageIsFinished,
            selectedTeamType: teamTypeFromState = "",
            setSelectedTeamType
        }
    } = useConfirmationPage();
    const { products } = useSearchParameters();

    const selectedTeamTypeAlreadyExists = teamTypeFromState?.length > 0;
    const shouldFetchSegmentData =
        userSegmentationPageIsFinished && !selectedTeamTypeAlreadyExists;
    const { fetched, teamType: teamTypeFromFetch } = useFetchUserSegmentData(
        shouldFetchSegmentData,
        setSelectedTeamType
    );

    useEffect(() => {
        if (!selectedTeamTypeAlreadyExists && teamTypeFromFetch) {
            setSelectedTeamType(teamTypeFromFetch);
        }
    }, [selectedTeamTypeAlreadyExists, teamTypeFromFetch, setSelectedTeamType]);

    const canShowJiraTemplateChoiceScreen = useMemo(() => {
        return (
            products.length === 1 && products[0] === "jira-software.ondemand"
        );
    }, [products]);

    return {
        setUserSegmentationPageIsFinished,
        canShowJiraTemplateChoiceScreen,
        loadingUserSegmentData: !fetched && !selectedTeamTypeAlreadyExists
    };
};

export const useJiraTemplateChoice = () => {
    return useContext(JiraTemplateChoiceContext);
};
